/**
 * MonthlySalesChart Component
 *
 * Month-over-month year-on-year comparison chart.
 * Shows current month actual sales (solid black), prediction (dotted black),
 * and same month from previous years (coloured lines).
 */

import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DashboardTabs } from '@/components/ui/dashboard-tabs'
import { TrendingUp } from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts'
import supabase from '../../supabase-client'

const YEAR_COLOURS = [
  '#9ca3af', // grey-400 (oldest)
  '#22c55e', // green-500
  '#ef4444', // red-500
  '#3b82f6', // blue-500
  '#f59e0b', // amber-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#14b8a6', // teal-500
]

const RANK_EMOJI = {
  1: '🚀',
  2: '✈️',
  3: '🚂',
  4: '🚢',
  5: '🚗',
  6: '🐴',
}

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate()
}

export default function MonthlySalesChart() {
  const [loading, setLoading] = useState(true)
  const [chartData, setChartData] = useState([])
  const [years, setYears] = useState([])
  const [rank, setRank] = useState(null)
  const [viewMode, setViewMode] = useState('week')

  // Year view state (lazy-loaded)
  const [yearChartData, setYearChartData] = useState([])
  const [yearYears, setYearYears] = useState([])
  const [yearRank, setYearRank] = useState(null)
  const [yearLoading, setYearLoading] = useState(false)
  const [yearLoaded, setYearLoaded] = useState(false)

  const now = new Date()
  const currentMonth = now.getMonth() // 0-indexed
  const currentYear = now.getFullYear()
  const currentDay = now.getDate()
  const monthName = now.toLocaleDateString('en-GB', { month: 'long' })
  const daysInMonth = getDaysInMonth(currentYear, currentMonth)

  // End of current week (Sunday) — cap at end of month
  const dayOfWeek = now.getDay() // 0=Sun, 1=Mon...6=Sat
  const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek

  useEffect(() => {
    fetchMonthData()
  }, [])

  useEffect(() => {
    if (viewMode === 'year' && !yearLoaded && !yearLoading) {
      fetchYearData()
    }
  }, [viewMode, yearLoaded, yearLoading])

  const fetchMonthData = async () => {
    try {
      setLoading(true)

      // Build OR filter for this month across last 6 years
      const monthStr = String(currentMonth + 1).padStart(2, '0')
      const orClauses = []
      for (let y = currentYear - 5; y <= currentYear; y++) {
        const lastDay = getDaysInMonth(y, currentMonth)
        orClauses.push(`and(date.gte.${y}-${monthStr}-01,date.lte.${y}-${monthStr}-${lastDay})`)
      }

      const { data: sales, error } = await supabase
        .from('sales')
        .select(`
          id,
          date,
          sale_items (
            quantity,
            price
          )
        `)
        .or(orClauses.join(','))
        .order('date', { ascending: true })

      if (error) throw error

      const salesByYearDay = new Map()
      const yearsFound = new Set()

      sales?.forEach(sale => {
        const saleDate = new Date(sale.date + 'T00:00:00')
        const year = saleDate.getFullYear()
        const day = saleDate.getDate()
        yearsFound.add(year)

        const key = `${year}-${day}`
        let saleTotal = 0
        sale.sale_items?.forEach(item => {
          saleTotal += (item.quantity || 0) * (item.price || 0)
        })

        salesByYearDay.set(key, (salesByYearDay.get(key) || 0) + saleTotal)
      })

      const sortedYears = Array.from(yearsFound).sort((a, b) => a - b)
      setYears(sortedYears)

      // Build cumulative totals for current year to fit regression
      let runningTotal = 0
      const cumulativePoints = [] // [{ x: day, y: cumTotal }]
      for (let d = 1; d <= currentDay; d++) {
        runningTotal += salesByYearDay.get(`${currentYear}-${d}`) || 0
        cumulativePoints.push({ x: d, y: runningTotal })
      }

      // Least-squares linear regression on cumulative data
      const n = cumulativePoints.length
      let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0
      cumulativePoints.forEach(({ x, y }) => {
        sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x
      })
      const regSlope = n > 1 ? (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) : 0
      const regIntercept = n > 0 ? (sumY - regSlope * sumX) / n : 0

      // Anchor prediction: start at actual value, end at regression endpoint
      const actualAtToday = runningTotal
      const regressionAtEnd = regSlope * daysInMonth + regIntercept
      const predSlope = daysInMonth > currentDay
        ? (regressionAtEnd - actualAtToday) / (daysInMonth - currentDay)
        : 0

      // Build chart data: cumulative values per day of month
      const data = []
      const cumulative = {} // running totals per year
      const cumulativeAtCurrentDay = {} // totals at current day (for ranking)
      sortedYears.forEach(y => { cumulative[y] = 0 })

      for (let day = 1; day <= daysInMonth; day++) {
        const entry = { day }

        sortedYears.forEach(year => {
          const dailyValue = salesByYearDay.get(`${year}-${day}`) || 0
          cumulative[year] += dailyValue

          if (day === currentDay) {
            cumulativeAtCurrentDay[year] = cumulative[year]
          }

          if (year === currentYear) {
            if (day <= currentDay) {
              entry.actual = cumulative[year]
            }
            if (day >= currentDay) {
              entry.prediction = actualAtToday + predSlope * (day - currentDay)
            }
          } else {
            if (day <= currentDay) {
              entry[`y${year}`] = cumulative[year]
            }
            if (day >= currentDay) {
              entry[`y${year}ext`] = cumulative[year]
            }
          }
        })

        data.push(entry)
      }

      // Calculate rank: compare cumulative totals at currentDay across years
      const totalsByYear = sortedYears.map(year => ({
        year,
        total: cumulativeAtCurrentDay[year] || 0
      }))
      totalsByYear.sort((a, b) => b.total - a.total)
      const rankIndex = totalsByYear.findIndex(t => t.year === currentYear)
      setRank(rankIndex >= 0 ? rankIndex + 1 : null)

      setChartData(data)
    } catch (error) {
      console.error('Error fetching monthly sales data:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchYearData = async () => {
    try {
      setYearLoading(true)

      const orClauses = []
      for (let y = currentYear - 5; y <= currentYear; y++) {
        orClauses.push(`and(date.gte.${y}-01-01,date.lte.${y}-12-31)`)
      }

      // Paginate to bypass the default 1000-row limit (full-year fetch across 6 years)
      const sales = []
      const pageSize = 1000
      let from = 0
      while (true) {
        const { data: page, error } = await supabase
          .from('sales')
          .select(`
            id,
            date,
            sale_items ( quantity, price )
          `)
          .or(orClauses.join(','))
          .order('date', { ascending: true })
          .range(from, from + pageSize - 1)

        if (error) throw error
        if (!page || page.length === 0) break
        sales.push(...page)
        if (page.length < pageSize) break
        from += pageSize
      }

      // Aggregate by year + month (0-indexed)
      const salesByYearMonth = new Map()
      const yearsFound = new Set()

      sales?.forEach(sale => {
        const saleDate = new Date(sale.date + 'T00:00:00')
        const y = saleDate.getFullYear()
        const m = saleDate.getMonth()
        yearsFound.add(y)
        const key = `${y}-${m}`
        let total = 0
        sale.sale_items?.forEach(item => {
          total += (item.quantity || 0) * (item.price || 0)
        })
        salesByYearMonth.set(key, (salesByYearMonth.get(key) || 0) + total)
      })

      const sortedYears = Array.from(yearsFound).sort((a, b) => a - b)
      setYearYears(sortedYears)

      // Day-of-year for "today" to anchor regression on partial current month
      const startOfYear = new Date(currentYear, 0, 1)
      const dayOfYear = Math.floor((now - startOfYear) / 86400000) + 1

      // Build cumulative by month, ending each month at month-end
      // For current year, current month is partial — split it
      const data = []
      const cumulative = {}
      const cumulativeAtCurrentMonth = {}
      sortedYears.forEach(y => { cumulative[y] = 0 })

      // Cumulative current-year total at "today" for regression
      let currentYearToToday = 0
      for (let m = 0; m < currentMonth; m++) {
        currentYearToToday += salesByYearMonth.get(`${currentYear}-${m}`) || 0
      }
      // Pro-rate the current month based on days elapsed
      const currentMonthSoFar = salesByYearMonth.get(`${currentYear}-${currentMonth}`) || 0
      currentYearToToday += currentMonthSoFar

      // Linear regression on (dayOfYear, currentYearToToday) anchored at origin? Simpler: slope = total/dayOfYear, predict to year-end
      const dailyRate = dayOfYear > 0 ? currentYearToToday / dayOfYear : 0

      for (let m = 0; m < 12; m++) {
        const entry = { month: m }

        sortedYears.forEach(year => {
          const monthly = salesByYearMonth.get(`${year}-${m}`) || 0
          cumulative[year] += monthly

          if (m === currentMonth) {
            cumulativeAtCurrentMonth[year] = cumulative[year]
          }

          if (year === currentYear) {
            if (m < currentMonth) {
              entry.actual = cumulative[year]
            } else if (m === currentMonth) {
              entry.actual = cumulative[year]
              entry.prediction = cumulative[year]
            } else {
              // Prediction: extrapolate at dailyRate to end of this month
              const endOfMonthDate = new Date(currentYear, m + 1, 0)
              const endDayOfYear = Math.floor((endOfMonthDate - startOfYear) / 86400000) + 1
              entry.prediction = currentYearToToday + dailyRate * (endDayOfYear - dayOfYear)
            }
          } else {
            if (m <= currentMonth) entry[`y${year}`] = cumulative[year]
            if (m >= currentMonth) entry[`y${year}ext`] = cumulative[year]
          }
        })

        data.push(entry)
      }

      // Rank: compare cumulative at currentMonth across years
      const totalsByYear = sortedYears.map(year => ({
        year,
        total: cumulativeAtCurrentMonth[year] || 0
      }))
      totalsByYear.sort((a, b) => b.total - a.total)
      const rankIndex = totalsByYear.findIndex(t => t.year === currentYear)
      setYearRank(rankIndex >= 0 ? rankIndex + 1 : null)

      setYearChartData(data)
      setYearLoaded(true)
    } catch (error) {
      console.error('Error fetching yearly sales data:', error)
    } finally {
      setYearLoading(false)
    }
  }

  // Find Mondays in the current month for week separator lines
  const mondayDays = useMemo(() => {
    const days = []
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(currentYear, currentMonth, d)
      if (date.getDay() === 1 && d > 1) { // Monday, skip if 1st
        days.push(d)
      }
    }
    return days
  }, [currentYear, currentMonth, daysInMonth])

  const priorYears = useMemo(
    () => years.filter(y => y !== currentYear),
    [years, currentYear]
  )

  const priorYearsYear = useMemo(
    () => yearYears.filter(y => y !== currentYear),
    [yearYears, currentYear]
  )

  const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  const isYear = viewMode === 'year'
  const titleLabel = isYear ? `${currentYear}` : monthName
  const activeRank = isYear ? yearRank : rank
  const activeLoading = isYear ? yearLoading : loading
  const activeData = isYear ? yearChartData : chartData

  const lastVisibleDay = viewMode === 'month' ? daysInMonth : Math.min(currentDay + daysUntilSunday, daysInMonth)

  const visibleData = useMemo(
    () => chartData.filter(d => d.day <= lastVisibleDay),
    [chartData, lastVisibleDay]
  )

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null

    const day = label
    const isBeforeToday = day < currentDay
    const isToday = day === currentDay
    const filtered = payload.filter(entry => {
      const key = entry.dataKey
      const isExt = key.endsWith('ext')

      // Before today: show prior year main lines + actual only
      if (isBeforeToday) return !isExt && key !== 'prediction'

      // On today: show prior year main lines + actual only (not prediction)
      if (isToday) return !isExt && key !== 'prediction'

      // After today: show ext lines + prediction (not main prior year lines)
      return (isExt || key === 'prediction') && key !== 'actual'
    })

    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{label} {monthName}</p>
        {filtered.map((entry, index) => {
          let name = entry.dataKey
          if (name === 'actual') name = `${currentYear}`
          else if (name === 'prediction') name = `${currentYear}*`
          else if (name.endsWith('ext')) name = name.slice(1, -3)
          else if (name.startsWith('y')) name = name.slice(1)

          return (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground">{name}</span>
              </div>
              <span className="font-medium">{formatCurrency(entry.value)}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const YearTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null

    const m = label
    const isBeforeCurrent = m < currentMonth
    const isCurrent = m === currentMonth

    const filtered = payload.filter(entry => {
      const key = entry.dataKey
      const isExt = key.endsWith('ext')
      if (isBeforeCurrent) return !isExt && key !== 'prediction'
      if (isCurrent) return !isExt && key !== 'prediction'
      return (isExt || key === 'prediction') && key !== 'actual'
    })

    return (
      <div className="bg-white border border-border rounded-lg p-3 shadow-md">
        <p className="text-sm font-medium mb-2">{MONTH_LABELS[m]} {currentYear}</p>
        {filtered.map((entry, index) => {
          let name = entry.dataKey
          if (name === 'actual') name = `${currentYear}`
          else if (name === 'prediction') name = `${currentYear}*`
          else if (name.endsWith('ext')) name = name.slice(1, -3)
          else if (name.startsWith('y')) name = name.slice(1)

          return (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
                <span className="text-muted-foreground">{name}</span>
              </div>
              <span className="font-medium">{formatCurrency(entry.value)}</span>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Card className="flex flex-col border-0 shadow-none rounded-none sm:border sm:shadow-sm sm:rounded-xl">
      <CardHeader className="pb-3 px-3 sm:px-6">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-muted-foreground" />
          {titleLabel} Sales Comparison{activeRank !== null && ` — #${activeRank} ${RANK_EMOJI[activeRank] || ''}`}
        </CardTitle>
        <div className="flex justify-center mt-2">
          <DashboardTabs
            tabs={[
              { value: 'week', label: 'Week' },
              { value: 'month', label: 'Month' },
              { value: 'year', label: 'Year' },
            ]}
            value={viewMode}
            onValueChange={setViewMode}
            size="small"
          />
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col px-3 sm:px-6">
        {activeLoading ? (
          <div className="flex items-center justify-center flex-1 min-h-64">
            <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          </div>
        ) : isYear ? (
          activeData.length === 0 ? (
            <div className="flex items-center justify-center flex-1 min-h-64 text-muted-foreground text-sm">
              No sales data available for {currentYear}
            </div>
          ) : (
            <div className="flex-1 min-h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <LineChart data={yearChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={(m) => MONTH_LABELS[m]}
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                    interval={0}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `£${value.toLocaleString('en-GB')}`}
                    width={60}
                  />
                  <Tooltip content={<YearTooltip />} />

                  {/* Quarter separators */}
                  {[3, 6, 9].map(m => (
                    <ReferenceLine
                      key={`q-${m}`}
                      x={m}
                      stroke="hsl(var(--border))"
                      strokeDasharray="4 3"
                      strokeWidth={1}
                    />
                  ))}

                  {/* Prior year solid lines (up to current month) */}
                  {priorYearsYear.map((year, i) => (
                    <Line
                      key={year}
                      type="monotone"
                      dataKey={`y${year}`}
                      name={String(year)}
                      stroke={YEAR_COLOURS[i % YEAR_COLOURS.length]}
                      strokeWidth={1.5}
                      dot={false}
                      connectNulls
                    />
                  ))}

                  {/* Prior year extended lines (half opacity beyond current month) */}
                  {priorYearsYear.map((year, i) => (
                    <Line
                      key={`${year}-ext`}
                      type="monotone"
                      dataKey={`y${year}ext`}
                      name={`${year} (ext)`}
                      stroke={YEAR_COLOURS[i % YEAR_COLOURS.length]}
                      strokeWidth={1.5}
                      strokeOpacity={0.5}
                      dot={false}
                      connectNulls
                      legendType="none"
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="actual"
                    name={`${currentYear} (actual)`}
                    stroke="hsl(var(--foreground))"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="prediction"
                    name={`${currentYear} (predicted)`}
                    stroke="hsl(var(--foreground))"
                    strokeWidth={1}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )
        ) : visibleData.length === 0 ? (
          <div className="flex items-center justify-center flex-1 min-h-64 text-muted-foreground text-sm">
            No sales data available for {monthName}
          </div>
        ) : (
          <div className="flex-1 min-h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <LineChart data={visibleData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="hsl(var(--border))"
                  vertical={false}
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `£${value.toLocaleString('en-GB')}`}
                  width={60}
                />
                <Tooltip content={<CustomTooltip />} />

                {/* Week separator lines (Sunday/Monday boundary) */}
                {mondayDays.map(day => (
                  <ReferenceLine
                    key={`week-${day}`}
                    x={day}
                    stroke="hsl(var(--border))"
                    strokeDasharray="4 3"
                    strokeWidth={1}
                  />
                ))}

                {/* Prior year lines */}
                {priorYears.map((year, i) => (
                  <Line
                    key={year}
                    type="monotone"
                    dataKey={`y${year}`}
                    name={String(year)}
                    stroke={YEAR_COLOURS[i % YEAR_COLOURS.length]}
                    strokeWidth={1.5}
                    dot={false}
                    connectNulls
                  />
                ))}

                {/* Prior year extended lines (half opacity beyond current day) */}
                {priorYears.map((year, i) => (
                  <Line
                    key={`${year}-ext`}
                    type="monotone"
                    dataKey={`y${year}ext`}
                    name={`${year} (ext)`}
                    stroke={YEAR_COLOURS[i % YEAR_COLOURS.length]}
                    strokeWidth={1.5}
                    strokeOpacity={0.5}
                    dot={false}
                    connectNulls
                    legendType="none"
                  />
                ))}

                {/* Current month actual — solid black, thicker */}
                <Line
                  type="monotone"
                  dataKey="actual"
                  name={`${currentYear} (actual)`}
                  stroke="hsl(var(--foreground))"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />

                {/* Prediction (regression) — dotted black */}
                <Line
                  type="monotone"
                  dataKey="prediction"
                  name={`${currentYear} (predicted)`}
                  stroke="hsl(var(--foreground))"
                  strokeWidth={1}
                  strokeDasharray="6 4"
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
